import type {
  AutomationProposalSafety,
  AutomationRiskLevel,
  ProposedWorkflow,
} from '@/types/plan';

const BLOCKED_PATTERNS: Array<{ pattern: RegExp; reason: string }> = [
  { pattern: /\b(http_request|arbitrary http|http request|local services?|webhook[_ -]?(?:post|call|request|url)|(?:call|post)(?:\s+(?:a|an|the|this|that|to))*\s+webhook|arbitrary webhook|fetch\s+(?:https?:|url)|https?:\/\/)/i, reason: 'Arbitrary HTTP requests to local or external services are high-risk and need an advanced review path.' },
  { pattern: /\b(mqtt_publish|mqtt publish|publish(?:ing)?\s+.{0,32}\bmqtt|mqtt.{0,32}\b(?:publish|command|topic)|command topic|mqtt:\/\/)/i, reason: 'Publishing arbitrary MQTT command topics can directly affect devices without clear Home Assistant validation.' },
  { pattern: /\b(shell_exec|ssh_command|execute shell|run shell|run command|terminal command|command line|ssh\s+(?:into|to|command)|execute (?:a |an )?(?:terminal|ssh|shell) command)\b/i, reason: 'Shell or SSH command execution is outside the MVP Home Assistant proposal boundary.' },
];

const REVIEW_PATTERNS: Array<{ pattern: RegExp; reason: string }> = [
  { pattern: /\b(?:(?:lock|unlock).{0,32}\b(?:door|deadbolt|smart locks?|locks?)|(?:door|deadbolt|smart locks?|locks?).{0,32}\b(?:lock|unlock)|lock state)\b/i, reason: 'Changing lock state affects physical security.' },
  { pattern: /\block\.(?:lock|unlock)\b/i, reason: 'Changing lock state affects physical security.' },
  { pattern: /\b(?:(?:open|close).{0,32}\b(?:garage doors?|gates?|covers?|blinds?|shades?|doors?)|(?:garage doors?|gates?|covers?|blinds?|shades?|doors?).{0,32}\b(?:open|close))\b/i, reason: 'Opening or closing doors, gates, covers, or blinds can affect access and safety.' },
  { pattern: /\bcover\.(?:open|close)_cover\b/i, reason: 'Opening or closing doors, gates, covers, or blinds can affect access and safety.' },
  { pattern: /\b(disable|turn off|deactivate|arm|disarm).{0,32}\b(alarms?|cameras?|security|security systems?|surveillance)\b/i, reason: 'Changing alarm, camera, or security automation state affects home security.' },
  { pattern: /\balarm_control_panel\.alarm_(?:arm|disarm)(?:_[a-z0-9_]+)?\b/i, reason: 'Changing alarm, camera, or security automation state affects home security.' },
  { pattern: /\balarm_(?:arm|disarm)(?:_[a-z0-9_]+)?\b/i, reason: 'Changing alarm, camera, or security automation state affects home security.' },
  { pattern: /\b(hvac|climate|thermostat|heat|cool|heating|cooling|emergency heat|set temp|set temperature|change temperature|adjust temperature|raise temperature|lower temperature)\b/i, reason: 'HVAC changes can affect comfort, energy usage, equipment, and safety.' },
  { pattern: /\b(irrigation|sprinklers?|hydrawise|water zones?|all zones|long duration|water.{0,20}\b(?:zone|zones|lawn|yard|garden)|run.{0,32}\b(?:all zones|sprinklers?))\b/i, reason: 'Irrigation actions can waste water or damage property if duration or zones are wrong.' },
  { pattern: /\b(power[- ]?cycle|reboot|restart|turn off|shutdown|shut down).{0,40}\b(routers?|network devices?|network|wi[- ]?fi|internet|modem|gateway|switch|infrastructure|servers?)\b/i, reason: 'Power cycling network or infrastructure devices can break local control and connectivity.' },
];

const CONTROL_PATTERNS: Array<{ pattern: RegExp; reason: string }> = [
  { pattern: /\bhome[_ -]?assistant[_ -]?service(?:[_ -]?call)?\b|\bhome assistant.{0,24}\b(?:service|call)\b/i, reason: 'Generic Home Assistant service calls need review before they can affect devices or automations.' },
  { pattern: /\b(service call|call service|call a service|call the service|call home assistant service)\b/i, reason: 'Generic service execution needs review unless it is modeled as a read-only or notification-only flow.' },
  { pattern: /\b(kasa_device_control|device control|run automation|execute automation|activate scene|turn on|turn off|toggle|switch on|switch off|switch to)\b/i, reason: 'The proposal controls a physical device or automation rather than only reading or notifying.' },
  { pattern: /\b(?:turn|switch)\s+(?:(?:a|an|the|this|that|my|our)\s+)?(?:[a-z0-9_-]+\s+){0,5}(?:lights?|lamps?|fans?|outlets?|switches?|plugs?|devices?|appliances?|valves?|sprinklers?|irrigation)\s+(?:on|off)\b/i, reason: 'The proposal controls a physical device or automation rather than only reading or notifying.' },
  { pattern: /\b(?:run|execute|start)\s+(?:(?:a|an|the|this|that|my|our)\s+)?(?:[a-z0-9_-]+\s+){0,5}automations?\b/i, reason: 'The proposal controls a physical device or automation rather than only reading or notifying.' },
  { pattern: /\b(?:activate|start)\s+(?:(?:a|an|the|this|that|my|our)\s+)?(?:[a-z0-9_-]+\s+){0,5}scenes?\b/i, reason: 'The proposal controls a physical device or automation rather than only reading or notifying.' },
];

const AMBIGUOUS_PHYSICAL_PATTERNS: Array<{ pattern: RegExp; reason: string }> = [
  { pattern: /\bswitch\b/i, reason: 'Ambiguous switch actions need review unless they are clearly read-only or informational.' },
];

const INFORMATIONAL_PATTERNS = /\b(read|show|list|status|get_status|query|monitor|notify|notification|email|sms|log|report)\b/i;
const SCHEDULE_PATTERNS = /\b(cron|schedule|scheduled|sunset|sunrise|timer|delay|every day|weekday|at \d{1,2})\b/i;
const CONDITION_PATTERNS = /\b(condition|if |after dark|before sunrise|sun_condition|threshold|only when|when )\b/i;

function collectText(value: unknown): string[] {
  if (value == null) return [];
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return [String(value)];
  }
  if (Array.isArray(value)) {
    return value.flatMap(collectText);
  }
  if (typeof value === 'object') {
    return Object.entries(value).flatMap(([key, nested]) => [key, ...collectText(nested)]);
  }
  return [];
}

function unique(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function summarizeConfig(config: Record<string, unknown> | undefined, fallback: string): string {
  if (!config || Object.keys(config).length === 0) return fallback;
  const fields = Object.entries(config)
    .slice(0, 6)
    .map(([key, value]) => `${key}: ${String(value)}`);
  return fields.length > 0 ? fields.join(', ') : fallback;
}

function extractKnownTargets(config: Record<string, unknown> | undefined, keyPattern: RegExp): string[] {
  if (!config) return [];
  const targets: string[] = [];

  const visit = (value: unknown): void => {
    if (value == null || typeof value !== 'object') return;
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    for (const [key, nested] of Object.entries(value)) {
      if (keyPattern.test(key) && typeof nested === 'string') {
        targets.push(nested);
      }
      visit(nested);
    }
  };

  visit(config);
  return unique(targets).slice(0, 8);
}

function buildProposalText(workflow: ProposedWorkflow): string {
  return [
    workflow.name,
    workflow.description,
    workflow.triggerType,
    ...collectText(workflow.triggerConfig),
    ...(workflow.steps ?? []).flatMap((step) => [step.type, step.description]),
    ...(workflow.tags ?? []),
  ].join(' ');
}

// Exported so the HA-automation-config risk adapter (`haAutomationSafety.ts`)
// can reuse the proven text-regex risk engine WITHOUT routing HA configs
// through `ProposedWorkflow` (roadmap Rule 9 / Audit §A2). Additive export
// only — no behavior change.
export function getRiskLevel(text: string): { riskLevel: AutomationRiskLevel; reasons: string[] } {
  const blockedReasons = BLOCKED_PATTERNS
    .filter(({ pattern }) => pattern.test(text))
    .map(({ reason }) => reason);
  if (blockedReasons.length > 0) {
    return { riskLevel: 'blocked', reasons: unique(blockedReasons) };
  }

  const reviewReasons = REVIEW_PATTERNS
    .filter(({ pattern }) => pattern.test(text))
    .map(({ reason }) => reason);
  if (reviewReasons.length > 0) {
    return { riskLevel: 'review_required', reasons: unique(reviewReasons) };
  }

  const controlReasons = CONTROL_PATTERNS
    .filter(({ pattern }) => pattern.test(text))
    .map(({ reason }) => reason);
  if (controlReasons.length > 0) {
    return { riskLevel: 'review_required', reasons: unique(controlReasons) };
  }

  if (INFORMATIONAL_PATTERNS.test(text)) {
    return { riskLevel: 'informational', reasons: ['The proposal appears limited to read-only, logging, or notification behavior.'] };
  }

  const ambiguousPhysicalReasons = AMBIGUOUS_PHYSICAL_PATTERNS
    .filter(({ pattern }) => pattern.test(text))
    .map(({ reason }) => reason);
  if (ambiguousPhysicalReasons.length > 0) {
    return { riskLevel: 'review_required', reasons: unique(ambiguousPhysicalReasons) };
  }

  return { riskLevel: 'low', reasons: ['No known high-risk home automation action was detected.'] };
}

export function classifyAutomationProposal(workflow: ProposedWorkflow): AutomationProposalSafety {
  const text = buildProposalText(workflow);
  const { riskLevel, reasons } = getRiskLevel(text);
  const approvalRequired = riskLevel === 'review_required' || riskLevel === 'blocked';
  const draftByDefault = approvalRequired;
  const disabledByDefault = approvalRequired;
  const triggerSummary = workflow.triggerType
    ? `${workflow.triggerType}${workflow.triggerConfig ? ` (${summarizeConfig(workflow.triggerConfig, 'configured')})` : ''}`
    : 'Not specified';
  const conditionSummary = CONDITION_PATTERNS.test(text)
    ? 'Conditions or timing constraints are present in the proposal and must be reviewed before acceptance.'
    : undefined;
  const scheduleSummary = SCHEDULE_PATTERNS.test(text)
    ? 'Schedule, sun event, timer, or delay behavior detected.'
    : undefined;
  const actionSummary = workflow.steps && workflow.steps.length > 0
    ? workflow.steps.map((step) => `${step.type}: ${step.description}`).join(' | ')
    : workflow.description;

  return {
    riskLevel,
    riskReasons: reasons,
    approvalRequired,
    draftByDefault,
    disabledByDefault,
    triggerSummary,
    conditionSummary,
    affectedDevices: extractKnownTargets(workflow.triggerConfig, /deviceId|device|target|switch/i),
    affectedEntities: extractKnownTargets(workflow.triggerConfig, /entityId|entity|service|topic|locationName|zone/i),
    actionSummary,
    scheduleSummary,
    rollbackNote: approvalRequired
      ? 'Keep the workflow as a disabled draft until a user reviews it. Roll back by leaving it unpublished, disabling its trigger, or deleting the draft.'
      : 'Roll back by disabling the workflow trigger or deleting the draft before publishing.',
  };
}

export function normalizeAutomationProposal(workflow: ProposedWorkflow): ProposedWorkflow {
  return {
    ...workflow,
    safety: classifyAutomationProposal(workflow),
  };
}
